/**
 * Phone helpers backed by libphonenumber-js (full country metadata).
 * Storage / API: E.164 with leading +.
 * UI state often keeps digits-only; normalizePhoneInput adds +.
 */
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

export function phoneDigits(raw: string): string {
  let d = String(raw ?? "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length === 11 && d.startsWith("8")) d = `7${d.slice(1)}`;
  return d;
}

export type CountryProfile = {
  /** Calling code digits, e.g. "7" */
  code: string;
  iso: CountryCode;
  nationalLen: number;
  groups: number[];
  label: string;
  short: string;
};

const PRIORITY_ISO: CountryCode[] = [
  "RU",
  "KZ",
  "BY",
  "UZ",
  "TJ",
  "KG",
  "AZ",
  "GE",
  "TM",
  "UA",
  "AM",
  "MD",
  "IN",
  "CN",
  "TR",
  "DE",
  "AE",
  "US",
  "GB",
];

const ISO_LABEL_RU: Partial<Record<CountryCode, string>> = {
  RU: "Россия",
  KZ: "Казахстан",
  BY: "Беларусь",
  UZ: "Узбекистан",
  TJ: "Таджикистан",
  KG: "Кыргызстан",
  AZ: "Азербайджан",
  GE: "Грузия",
  TM: "Туркменистан",
  UA: "Украина",
  AM: "Армения",
  MD: "Молдова",
  IN: "Индия",
  CN: "Китай",
  TR: "Турция",
  DE: "Германия",
  AE: "ОАЭ",
  US: "США",
  GB: "Великобритания",
};

function buildCountryProfile(iso: CountryCode): CountryProfile {
  const code = getCountryCallingCode(iso);
  const example = getExampleNumber(iso, examples);
  const national = example?.nationalNumber?.replace(/\D/g, "") ?? "";
  const nationalLen = national.length || 10;
  // Heuristic groups for display fallback
  const groups =
    nationalLen === 10
      ? [3, 3, 2, 2]
      : nationalLen === 9
        ? [3, 3, 3]
        : nationalLen === 8
          ? [2, 2, 2, 2]
          : [3, 3, 4];
  return {
    code,
    iso,
    nationalLen,
    groups,
    label: ISO_LABEL_RU[iso] ?? iso,
    short: iso,
  };
}

const ALL_PROFILES: CountryProfile[] = getCountries().map(buildCountryProfile);

/** Display order: priority CIS / partners first, then A–Z by label. */
export const PHONE_COUNTRY_OPTIONS: CountryProfile[] = (() => {
  const byIso = new Map(ALL_PROFILES.map((p) => [p.iso, p]));
  const seen = new Set<string>();
  const out: CountryProfile[] = [];
  for (const iso of PRIORITY_ISO) {
    const p = byIso.get(iso);
    if (p) {
      out.push(p);
      seen.add(p.iso);
    }
  }
  const rest = ALL_PROFILES.filter((p) => !seen.has(p.iso)).sort((a, b) =>
    a.label.localeCompare(b.label, "ru"),
  );
  return [...out, ...rest];
})();

const PARSE_ORDER = [...PHONE_COUNTRY_OPTIONS].sort((a, b) => b.code.length - a.code.length);

export function getCountryProfile(code: string): CountryProfile | undefined {
  // +7 is shared by RU and KZ — default Russia (explicit KZ via ISO / flag select).
  if (code === "7") {
    return PHONE_COUNTRY_OPTIONS.find((p) => p.iso === "RU");
  }
  return PHONE_COUNTRY_OPTIONS.find((p) => p.code === code);
}

export function getCountryProfileByIso(iso: string): CountryProfile | undefined {
  return PHONE_COUNTRY_OPTIONS.find((p) => p.iso === iso);
}

export function formatNationalGroups(national: string, groups: number[]): string {
  if (!national) return "";
  let pos = 0;
  const parts: string[] = [];
  for (let i = 0; i < groups.length && pos < national.length; i++) {
    const size = groups[i]!;
    const chunk = national.slice(pos, Math.min(pos + size, national.length));
    pos += chunk.length;
    parts.push(chunk);
    if (chunk.length < size) break;
  }
  if (pos < national.length) parts.push(national.slice(pos));

  const first = parts[0]!;
  const firstComplete = first.length === groups[0];
  let out = `(${first}${firstComplete ? ")" : ""}`;
  if (parts.length === 1) return out;
  out += ` ${parts[1]}`;
  for (let i = 2; i < parts.length; i++) out += `-${parts[i]}`;
  return out;
}

export function formatNationalDisplay(cc: string, nationalRaw: string): string {
  const profile = getCountryProfile(cc) ?? PHONE_COUNTRY_OPTIONS[0]!;
  const asYou = new AsYouType(profile.iso);
  const formatted = asYou.input(phoneDigits(nationalRaw));
  if (formatted) {
    // Strip country prefix if AsYouType included it
    const nat = formatted.replace(new RegExp(`^\\+?${profile.code}\\s*`), "").trim();
    return nat || formatNationalGroups(phoneDigits(nationalRaw).slice(0, profile.nationalLen), profile.groups);
  }
  return formatNationalGroups(phoneDigits(nationalRaw).slice(0, profile.nationalLen), profile.groups);
}

export function nationalPlaceholder(cc?: string | null): string {
  const profile = getCountryProfile(cc || "7") ?? PHONE_COUNTRY_OPTIONS[0]!;
  const example = getExampleNumber(profile.iso, examples);
  if (example) {
    const asYou = new AsYouType(profile.iso);
    const f = asYou.input(example.nationalNumber);
    return f.replace(new RegExp(`^\\+?${profile.code}\\s*`), "").trim() || "(999) 000-00-00";
  }
  return "(999) 000-00-00";
}

export function splitPhoneDigits(
  raw: string,
  preferredCc?: string | null,
): { countryCode: string; national: string } {
  const parsed = tryParse(raw, preferredCc);
  if (parsed) {
    return {
      countryCode: parsed.countryCallingCode,
      national: parsed.nationalNumber,
    };
  }

  let d = phoneDigits(raw);
  if (preferredCc && getCountryProfile(preferredCc)) {
    if (d.startsWith(preferredCc)) {
      return { countryCode: preferredCc, national: d.slice(preferredCc.length) };
    }
    return { countryCode: preferredCc, national: d };
  }

  for (const p of PARSE_ORDER) {
    if (d.startsWith(p.code) && d.length > p.code.length) {
      return { countryCode: p.code, national: d.slice(p.code.length) };
    }
  }
  return { countryCode: "7", national: d };
}

function tryParse(raw: string, preferredCc?: string | null) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return undefined;

  if (trimmed.startsWith("+")) {
    const d = phoneDigits(trimmed);
    if (!d) return undefined;
    // Bare "+7" / "+375" — not a complete subscriber number
    const parsedCc = parsePhoneNumberFromString(`+${d}`);
    if (parsedCc && !parsedCc.nationalNumber) return undefined;
    return parsePhoneNumberFromString(trimmed);
  }

  const d = phoneDigits(trimmed);
  if (!d) return undefined;

  const preferredIso = (preferredCc && getCountryProfile(preferredCc)?.iso) || ("RU" as CountryCode);
  const preferredCalling = getCountryCallingCode(preferredIso);

  // Digits are only the calling code — empty national (avoids parse("7","RU") → +77)
  if (d === preferredCalling) return undefined;

  if (d.startsWith(preferredCalling) && d.length > preferredCalling.length) {
    return parsePhoneNumberFromString(`+${d}`);
  }

  // National-only
  if (d.length >= 8) {
    return parsePhoneNumberFromString(d, preferredIso);
  }

  // Incomplete national — still build E.164 for controlled input display
  return parsePhoneNumberFromString(`+${preferredCalling}${d}`);
}

export function toE164Value(raw: string, lockedCc?: string | null): string | undefined {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return undefined;
  const d = phoneDigits(trimmed);
  if (!d) return undefined;
  const cc = lockedCc || "7";
  if (d === cc) return undefined;
  const parsed = tryParse(raw, lockedCc);
  if (parsed?.number) return parsed.number;
  if (d.startsWith(cc) && d.length > cc.length) return `+${d}`;
  return `+${cc}${d}`;
}

export function joinPhoneDigits(countryCode: string, nationalRaw: string): string {
  const profile = getCountryProfile(countryCode) ?? PHONE_COUNTRY_OPTIONS[0]!;
  const national = phoneDigits(nationalRaw).slice(0, Math.max(profile.nationalLen, 15));
  return `${profile.code}${national}`;
}

export function formatPhoneDisplay(rawDigits: string, lockedCc?: string | null): string {
  const parsed = tryParse(rawDigits, lockedCc);
  if (parsed) return parsed.formatInternational();
  const { countryCode, national } = splitPhoneDigits(rawDigits, lockedCc);
  const nat = formatNationalDisplay(countryCode, national);
  return nat ? `+${countryCode} ${nat}` : `+${countryCode}`;
}

export function normalizePhoneInput(raw: string, lockedCc?: string | null): string {
  const parsed = tryParse(raw, lockedCc);
  if (parsed) return parsed.number;
  const { countryCode, national } = splitPhoneDigits(raw, lockedCc);
  const joined = joinPhoneDigits(countryCode, national);
  return joined ? `+${joined}` : "";
}

export function isCompletePhoneInput(raw: string, lockedCc?: string | null): boolean {
  const e164 = normalizePhoneInput(raw, lockedCc);
  if (!e164) return false;
  if (isValidPhoneNumber(e164)) return true;
  // Fallback: national length for known profile (lib may reject some edge CIS formats)
  const { countryCode, national } = splitPhoneDigits(raw, lockedCc);
  const profile = getCountryProfile(countryCode);
  return Boolean(profile && national.length >= Math.min(profile.nationalLen, 8));
}

/** @deprecated use PHONE_COUNTRY_OPTIONS */
export const PHONE_CODE_CHIPS = PHONE_COUNTRY_OPTIONS.map((p) => ({
  code: p.code,
  label: `+${p.code}`,
}));

export function placeholderForCountry(cc?: string | null): string {
  const code = cc || "7";
  return `+${code} ${nationalPlaceholder(code)}`;
}

export function inferLockedCountry(digits: string): string | null {
  const parsed = tryParse(digits);
  if (parsed?.countryCallingCode) return parsed.countryCallingCode;
  const d = phoneDigits(digits);
  if (!d) return null;
  for (const p of PARSE_ORDER) {
    if (d.startsWith(p.code)) return p.code;
  }
  if (d.length <= 10) return "7";
  return null;
}
